import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withFieldProps } from 'hooks/useFieldProps';
import { withStyles } from 'theme/withStyles';
import FlyupLabel from 'common/FlyupLabel';
import Spinner from 'common/Spinner';
import ErrorMsg from 'common/ErrorMsg';
import Help from 'common/Help';
import Tooltip from 'common/Tooltip';

import rootStyles from './styles/root';
import inputStyles from './styles/input';

const styles = ({ forms, palette, spacing, typography }) => ({
  hiddenInput: {
    ...inputStyles({ forms, palette, typography }),
    background: 'none',
    border: 'none',
    fontFamily: 'Inconsolata, monospace',
    left: 0,
    marginTop: 0,
    pointerEvents: 'none',
    position: 'absolute',
    top: 0,
  },
  input: {
    ...inputStyles({ forms, palette, typography }),
    '&::selection': {
      backgroundColor: '#B3D7FF',
      color: 'transparent',
    },
    caretColor: palette.text,
    color: 'transparent',
    fontFamily: 'Inconsolata, monospace',
  },
  root: rootStyles({ spacing }),
});

const MaskedSecretString = ({
  classes,
  error,
  fieldDomId,
  format,
  help,
  inputmode,
  loading,
  name,
  onBlur,
  onChange,
  onDisable,
  onFocus,
  onPaste,
  placeholder,
  readOnly,
  required,
  tooltip,
  type,
  validate,
  value,
}) => {
  let maskedValue = '';
  let unMaskedValue = '';

  if (value) {
    let k = 0;
    for (let i = 0; i < format.length; i += 1) {
      if (!value[k]) {
        break;
      } else if (format[i].match(/[*\d\w]/)) {
        maskedValue += format[i] === '*' ? '*' : value[k];
        unMaskedValue += value[k];
        k += 1;
      } else {
        maskedValue += format[i];
        unMaskedValue += format[i];
      }
    }
  }
  return (
    <>
      <div className={classnames('form-group', { error }, classes.root)}>
        <Spinner loading={loading} />
        <FlyupLabel
          fieldName={name}
          error={error}
          value={value}
          htmlFor={fieldDomId}
          required={required}
          tooltip={tooltip}
        />
        <input
          type={type}
          inputMode={inputmode}
          className={classnames('form-control', 'apply-secret-mask', classes.input)}
          name={name}
          id={fieldDomId}
          value={unMaskedValue}
          placeholder={placeholder}
          readOnly={readOnly}
          required={required}
          aria-describedby={
            classnames({
              [`help-${name}`]: help,
              [`error-${name}`]: error,
            }) || undefined
          }
          aria-invalid={error}
          onChange={(e) => {
            const { value: targetValue } = e.target;
            if (targetValue.length > format.length) {
              return;
            }
            onChange(name, targetValue.replace(/[^0-9]/g, ''));
          }}
          onFocus={onFocus}
          onBlur={() => {
            onBlur();
            validate();
          }}
          onPaste={onPaste}
          autoComplete="off"
          onCut={onDisable}
          onCopy={onDisable}
          onContextMenu={onDisable}
        />
        <input
          value={maskedValue}
          type="text"
          onChange={() => {}}
          className={classnames('secret-format', classes.hiddenInput)}
          autoComplete="off"
          tabIndex="-1"
        />
        <Tooltip tooltip={tooltip} labeledBy={`label-${fieldDomId}`} />
        <Help help={help} id={`help-${name}`} />
      </div>

      <ErrorMsg name={name} />
    </>
  );
};

MaskedSecretString.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  error: PropTypes.bool.isRequired,
  fieldDomId: PropTypes.string.isRequired,
  format: PropTypes.string.isRequired,
  help: PropTypes.string.isRequired,
  inputmode: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onDisable: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  onPaste: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  readOnly: PropTypes.bool.isRequired,
  required: PropTypes.bool.isRequired,
  tooltip: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  validate: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

const MaskedSecretStringStyled = withStyles(styles)(MaskedSecretString);

export default withFieldProps()(MaskedSecretStringStyled);
